import type { QueryClient } from '@tanstack/react-query'
import type { Dispatch, RefObject } from 'react'
import { useEffect } from 'react'
import type { ReportingDraftAction } from 'src/agent/reporting/conversation/reportingDraft.reducer'
import type { ReportingRunProjection } from 'src/queries/reportingAgent/reportingAgent.models'
import { reportingAgentQueries } from 'src/queries/reportingAgent/reportingAgent.queries'
import {
  ReportingAgentAccessDeniedError,
  streamReportingRunEvents,
} from 'src/queries/reportingAgent/reportingAgent.sse'

import {
  isReportingAccessDenied,
  reportingErrorStatus,
} from './reportingConversation.errors'

interface UseReportingRunStreamOptions {
  activeConversationId?: string
  actorId?: string
  organizationId?: string
  isAuthenticated: boolean
  accessDeniedRef: RefObject<boolean>
  queryClient: QueryClient
  run?: ReportingRunProjection
  refetchRun: () => Promise<{ data?: ReportingRunProjection }>
  refetchMessages: () => Promise<unknown>
  dispatch: Dispatch<ReportingDraftAction>
  startStream: () => AbortSignal
  stopStream: () => void
  getStreamSequence: () => number
  stream: typeof streamReportingRunEvents
  denyAccess: (status?: number) => void
}

/** Reconciles one durable run stream with canonical query state. */
export const useReportingRunStream = ({
  activeConversationId,
  actorId,
  organizationId,
  isAuthenticated,
  accessDeniedRef,
  queryClient,
  run,
  refetchRun,
  refetchMessages,
  dispatch,
  startStream,
  stopStream,
  getStreamSequence,
  stream,
  denyAccess,
}: UseReportingRunStreamOptions): void => {
  useEffect(() => {
    if (
      !organizationId ||
      !run ||
      run.status === 'completed' ||
      accessDeniedRef.current
    ) {
      return
    }
    const signal = startStream()
    let cancelled = false
    let afterSequence = getStreamSequence()
    let transientRetries = 0
    const fetchArtifact = async (artifactId: string): Promise<void> => {
      await queryClient.fetchQuery(
        reportingAgentQueries.draftDisplay({
          actorId,
          organizationId,
          conversationId: activeConversationId,
          artifactId,
          reportingEnabled: isAuthenticated,
        }),
      )
    }

    void (async () => {
      for (;;) {
        if (cancelled) return
        try {
          let terminal = false
          for await (const streamEvent of stream({
            organizationId,
            runId: run.id,
            afterSequence,
            signal,
            onEvent: (nextEvent) => {
              afterSequence = nextEvent.sequence
              dispatch({ type: 'eventReceived', event: nextEvent })
            },
          })) {
            if (cancelled) return
            transientRetries = 0
            if (streamEvent.terminal) {
              terminal = true
              const completedRun = await refetchRun()
              await refetchMessages()
              if (completedRun.data?.artifact_id) {
                await fetchArtifact(completedRun.data.artifact_id)
              }
            }
          }
          if (terminal || cancelled) return
          await Promise.resolve()
          if (cancelled) return
        } catch (error) {
          if (cancelled) return
          if (error instanceof ReportingAgentAccessDeniedError) {
            denyAccess(error.status)
            return
          }
          let reconciledRun: ReportingRunProjection | undefined
          try {
            const refreshedRun = await refetchRun()
            reconciledRun = refreshedRun.data
            await refetchMessages()
          } catch (reconciliationError) {
            if (isReportingAccessDenied(reconciliationError)) {
              denyAccess(reportingErrorStatus(reconciliationError) ?? 403)
              return
            }
          }
          if (
            reconciledRun?.status === 'completed' ||
            reconciledRun?.status === 'failed' ||
            reconciledRun?.status === 'cancelled'
          ) {
            if (reconciledRun.artifact_id) {
              await fetchArtifact(reconciledRun.artifact_id)
            }
            return
          }
          transientRetries += 1
          dispatch({ type: 'streamReconnected', afterSequence })
          await new Promise((resolve) =>
            setTimeout(
              resolve,
              Math.min(250 * 2 ** Math.min(transientRetries - 1, 4), 4_000),
            ),
          )
        }
      }
    })()
    return () => {
      cancelled = true
      stopStream()
    }
  }, [
    accessDeniedRef,
    activeConversationId,
    actorId,
    denyAccess,
    dispatch,
    getStreamSequence,
    isAuthenticated,
    organizationId,
    queryClient,
    refetchMessages,
    refetchRun,
    run,
    startStream,
    stopStream,
    stream,
  ])
}
